import type { ClanTask } from "../lib/api";
import { useT } from "../i18n";

type Task = ClanTask;

function CheckCircle({ done }: { done: boolean }) {
  if (done) {
    return (
      <span className="w-7 h-7 shrink-0 rounded-full bg-success flex items-center justify-center">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return <span className="w-7 h-7 shrink-0 rounded-full border-2 border-white/20" />;
}

export default function TaskList({ tasks, onOpen }: { tasks: Task[]; onOpen?: (key: string) => void }) {
  const { t } = useT();
  const doneCount = tasks.filter((task) => task.done).length;
  const total = tasks.length;
  const pct = total > 0 ? (doneCount / total) * 100 : 0;

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-bold">{t("tasks.title")}</h2>
        <span className="text-muted text-sm">
          {t("tasks.progress", { done: doneCount, total })}
        </span>
      </div>

      <div className="h-1.5 rounded-full bg-white/10 mb-4">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#8E6BFF] to-[#6E48F4] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="bg-card border border-line-2 rounded-[16px] divide-y divide-white/5">
        {tasks.map((task) =>
          onOpen ? (
            <button
              key={task.key}
              type="button"
              onClick={() => onOpen(task.key)}
              className="w-full text-left flex items-center gap-3 p-4 active:bg-white/[0.04] transition-colors"
            >
              <CheckCircle done={task.done} />
              <span className={`flex-1 text-sm ${task.done ? "text-muted line-through" : "text-white"}`}>{task.title}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0 opacity-60">
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : (
            <div key={task.key} className="flex items-center gap-3 p-4">
              <CheckCircle done={task.done} />
              <span className={`text-sm ${task.done ? "text-muted line-through" : "text-white"}`}>{task.title}</span>
            </div>
          ),
        )}
        {total === 0 && <div className="p-4 text-muted text-sm">{t("tasks.empty")}</div>}
      </div>
    </section>
  );
}
